import { useEffect, useMemo, useRef, useState } from "react";
import type { RefuelEvent } from "../types/strategy";
import type { Session } from "../types/session";
import type { ExternalAlert } from "./useAlerts";

export type StintState = "none" | "ok" | "warn" | "over";

export interface StintClock {
  startedAt: Date | null;
  elapsedMs: number;
  state: StintState;
}

const TICK_MS = 1_000;
const WARN_BEFORE_MS = 15 * 60_000;

function parseIso(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

export function resolveStintStart(
  activeSession: Pick<Session, "started_at"> | null,
  refuels: Pick<RefuelEvent, "detected_at">[],
): Date | null {
  if (!activeSession) return null;
  let latest: number | null = null;
  for (const refuel of refuels) {
    const t = parseIso(refuel.detected_at);
    if (t !== null && (latest === null || t > latest)) latest = t;
  }
  const start = latest ?? parseIso(activeSession.started_at);
  return start === null ? null : new Date(start);
}

export function deriveStintState(elapsedMs: number, targetMinutes: number): StintState {
  if (targetMinutes <= 0) return "ok";
  const targetMs = targetMinutes * 60_000;
  if (elapsedMs >= targetMs) return "over";
  if (elapsedMs >= targetMs - WARN_BEFORE_MS) return "warn";
  return "ok";
}

export function formatStintClock(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mmss = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

export function formatStintTarget(targetMinutes: number): string {
  const hours = Math.floor(targetMinutes / 60);
  const minutes = targetMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function useStintClock(
  activeSession: Session | null,
  refuels: RefuelEvent[],
  targetMinutes: number,
): StintClock {
  const startedAt = useMemo(
    () => resolveStintStart(activeSession, refuels),
    [activeSession, refuels],
  );
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, [startedAt]);

  if (!startedAt) return { startedAt: null, elapsedMs: 0, state: "none" };
  const elapsedMs = Math.max(0, now - startedAt.getTime());
  return { startedAt, elapsedMs, state: deriveStintState(elapsedMs, targetMinutes) };
}

export const STINT_TARGET_RULE_ID = "stint_target_reached";

export function useStintTargetAlert(
  stint: StintClock,
  targetMinutes: number,
  raise: (alert: ExternalAlert) => void,
): void {
  const prevStateRef = useRef<StintState>(stint.state);
  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = stint.state;
    if (stint.state !== "over" || prev === "over" || prev === "none") return;
    raise({
      ruleId: STINT_TARGET_RULE_ID,
      ruleName: "Stint target",
      severity: "warning",
      message: `Stint target reached (${formatStintTarget(targetMinutes)})`,
      value: Math.round(stint.elapsedMs / 60_000),
    });
  }, [stint.state, stint.elapsedMs, targetMinutes, raise]);
}
